import { api } from "@/lib/api";

import { queryOptions, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useToast } from "../use-toast";
import {
  HotelRoomPriceDto,
  hotelRoomPriceDtoListSchema,
  HotelRoomPriceDtoSchema,
  hotelRoomPriceFromDto,
} from "@/models/hotel-room-price";

export function getHotelRoomPricesQueryOptions(roomId: string) {
  return queryOptions({
    queryKey: ["hotel-room-prices", roomId],
    queryFn: async () => {
      const result: Array<HotelRoomPriceDto> = [];
      let page = 0;
      let hasNextPage = true;
      while (hasNextPage) {
        page += 1;
        const res = await api.getWithCache(
          "/api/hotel-room-prices",
          hotelRoomPriceDtoListSchema,
          {
            query: {
              page: page.toString(),
            },
            filters: { HotelRoomId: Number.parseInt(roomId) },
            projection: ["HotelRoom"],
          },
        );
        result.push(...res.Data);
        hasNextPage = res.PageInfo.HasNextPage;
      }
      return result.map(hotelRoomPriceFromDto);
    },
  });
}

export function useGetHotelRoomPrices<TERROR extends Error>(
  roomId: string,
  opts?: {
    onError?: (error: TERROR) => void;
    enabled?: boolean;
  },
) {
  const { toast } = useToast();

  const q = useQuery({
    ...getHotelRoomPricesQueryOptions(roomId),
    enabled: opts?.enabled,
  });
  useEffect(() => {
    if (!q.error) return;
    toast({
      title: "toast.getHotelRoomPricesError",
      variant: "destructive",
    });
    opts?.onError?.(q.error as TERROR);
  }, [opts, q.error, toast]);
  return q;
}

export const getAllHotelRoomPricesQueryOptions = queryOptions({
  queryKey: ["all-hotel-room-prices"],
  queryFn: async () => {
    const result: Array<HotelRoomPriceDto> = [];
    let page = 0;
    let hasNextPage = true;
    while (hasNextPage) {
      page += 1;
      const res = await api.getWithCache(
        "/api/hotel-room-prices",
        hotelRoomPriceDtoListSchema,
        {
          query: {
            page: page.toString(),
          },
          projection: ["HotelRoom"],
        },
      );
      result.push(...res.Data);
      hasNextPage = res.PageInfo.HasNextPage;
    }
    return result.map(hotelRoomPriceFromDto);
  },
});

export function useGetAllHotelRoomPrices(opts?: { enabled?: boolean }) {
  return useQuery({
    ...getAllHotelRoomPricesQueryOptions,
    enabled: opts?.enabled,
  });
}

export function getHotelRoomPriceQueryOptions(roomId: string, priceId: string) {
  return queryOptions({
    queryKey: ["hotel-room-price", roomId, priceId],
    queryFn: () =>
      api
        .get("/api/hotel-room-prices/$id", HotelRoomPriceDtoSchema, {
          pathReplace: { $id: priceId },
          filters: { HotelRoomId: Number.parseInt(roomId) },
          projection: ["HotelRoom"],
        })
        .then(hotelRoomPriceFromDto),
  });
}

export function useGetHotelRoomPrice<TERROR extends Error>(
  roomId: string,
  priceId: string,
  opts?: { onError?: (error: TERROR) => void; enabled?: boolean },
) {
  const { toast } = useToast();
  const q = useQuery({
    ...getHotelRoomPriceQueryOptions(roomId, priceId),
    enabled: opts?.enabled,
  });
  useEffect(() => {
    if (!q.error) return;
    toast({
      title: "toast.getHotelRoomPriceDetailError",
      variant: "destructive",
    });
    opts?.onError?.(q.error as TERROR);
  }, [opts, q.error, toast]);
  return q;
}
